import React, { useEffect } from 'react';
import { Card, CardHeader, Icon, Paragraph } from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../../../Localization';
import { ChatKit, useChatKit } from '@openai/chatkit-react';
import { useCookies } from 'react-cookie';
import { apiPath, concatPathAndQueryParams, get_deployment } from '../../../API';
import { logAdminPanelError } from '../../../utils/adminPanel.utils';

// Safe skeleton version of the program assistant
// Render this one when the program assistant is not enabled for a program
export const ProgramAssistantSafe = () => {
  const L = useLocalizedStrings();
  return (
    <Card className="p-1 w-full rounded-md">
      <CardHeader
        title={
          <div className="flex items-center gap-x-2">
            <Icon className="text-[var(--ak-identity-color-blue)]" name='DocumentTextIcon' type="solid" />
            <div className="font-bold text-xl">{L.admin_panel.program_home.program_assistant.title}</div>
          </div>
        }
      />
      <div>
        <Paragraph>{L.admin_panel.program_home.program_assistant.description}</Paragraph>
      </div>
    </Card>
  );
}

const CHATKIT_API_PATH = 'api/admin_panel/ai/create_chatkit_session'

export const ProgramAssistant = () => {
  const L = useLocalizedStrings();
  const [cookies] = useCookies(['auth_token']);


  // Load ChatKit script when component mounts
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdn.platform.openai.com/deployments/chatkit/chatkit.js';
    script.async = true;
    document.head.appendChild(script);

    // Cleanup function to remove script when component unmounts
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const { control } = useChatKit({
    api: {
      async getClientSecret() {
        // Get auth token from cookies
        // We can't use the AuthContext here because getClientSecret isn't called from within the AuthContext.
        const authToken = cookies.auth_token;
        if (!authToken) {
          // TODO: record error to Sentry here
          throw new Error('No authentication token found');
        }

        // Perform a manual fetch, passing in the proper auth token.
        const chatKitUrl = apiPath() + concatPathAndQueryParams(CHATKIT_API_PATH, ["token="+authToken, "program="+get_deployment()])

        const response = await fetch(chatKitUrl, {
          method: 'POST',
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({})
        });

        const resp = await response.json();

        if (resp && resp.success && resp.data) {
          const { client_secret } = resp.data

          return client_secret;
        } else if (resp.error) {
          logAdminPanelError(resp.error, resp.message)
          return
        }
      },
    },
    history: {
      enabled: false,
    },
    startScreen: {
      greeting: "\u200B",
      prompts: [
        {
          label: "Build me a report which shows all payments in the last month in San Francisco by zip code",
          prompt: "Build me a report which shows all payments in the last month in San Francisco by zip code",
          icon: 'write-alt2'
        },
        {
          label: "Show me applications from the southeast region for March 2024, grouped by county",
          prompt: "Show me applications from the southeast region for March 2024, grouped by county",
          icon: 'write-alt2'
        }
      ]
    },
    header: {
      enabled: false,
    },
    theme: {
      radius: 'round',
      color: {
        accent: {
          primary: '#2B2C60',
          level: 3
        }
      },
      density: 'compact'
    },
    widgets: {
      async onAction(action, item) {
        // TODO: this is hardcoded for now, we will need to uopdate this once it actually works
        if (action.type === 'report.generate' && action.payload && action.payload.link) {
          window.open(action.payload.link as string) 
        }
      },
    },
    composer: {
      placeholder: 'Describe the report you need...'
    }
  });

  return (
    <Card className="p-1 w-full rounded-md">
      <CardHeader
        title={
          <div className="flex items-center gap-x-2">
            <Icon className="text-[var(--ak-identity-color-blue)]" name='DocumentTextIcon' type="solid" />
            <div className="font-bold text-xl">{L.admin_panel.program_home.program_assistant.title}</div>
          </div>
        }
      />
      <div>
        <Paragraph>{L.admin_panel.program_home.program_assistant.description}</Paragraph>
      </div>
      <div className="h-96 rounded-md border p-1">
        <ChatKit
          control={control}
        />
      </div>
    </Card>
  );
};
